// Budget demo · segmentation + forecast — the substance (spec §4–§6).
//
// Pure functions shared by BOTH fixtures.ts (seed) and engine.ts (ingest), so
// adding contracts/POs recomputes through exactly the same code that built the
// seed. No randomness at call time: every "market" number is drawn from a
// deterministic PRNG seeded by the contract/category id, so the demo is stable
// across reloads.

import { formatPct } from "./format";
import {
    BudgetLine,
    budgetLineId,
    Category,
    CategoryId,
    CommercialStrategy,
    Confidence,
    Contract,
    contractId,
    Money,
    RenewalPlaybook,
    Segment,
    Tone,
    usd,
} from "./types";
import { WpcProject } from "./projects";

// ---------------------------------------------------------------------------
// Deterministic PRNG (mulberry32) — keyed by a string so results are stable.
// ---------------------------------------------------------------------------
export function hashSeed(s: string): number {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/** Per-category tail trend default — varied 2%–6% (deterministic from the
 * category id) so categories don't all show the same number. Shared by the
 * Budget and Track tabs via BudgetContext so the budgeted tail growth matches. */
export const defaultTailTrend = (categoryId: string): number =>
    (2 + (hashSeed(`${categoryId}:tailtrend`) % 5)) / 100;

export function mulberry32(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// ---------------------------------------------------------------------------
// Segment metadata — label + pill tone.
// ---------------------------------------------------------------------------
export const SEGMENT_META: Record<Segment, { label: string; tone: Tone }> = {
    under_contract: { label: "Under contract", tone: "blue" },
    up_for_renewal: { label: "Up for renewal", tone: "amber" },
    tail: { label: "Tail", tone: "slate" },
    new_spend: { label: "New spend", tone: "teal" },
};

export const SEGMENT_ORDER: Segment[] = [
    "under_contract",
    "up_for_renewal",
    "tail",
    "new_spend",
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Last 4-digit run in a date label: "Mar 31, 2026" → 2026. */
function parseYear(label: string): number | null {
    const m = label.match(/\d{4}/g);
    return m ? parseInt(m[m.length - 1], 10) : null;
}

const MONTH_INDEX: Record<string, number> = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

/** Fiscal quarter a date label falls in: "Mar 31, 2027" → "Q1 2027". Returns
 * null for non-dated labels ("rolling", year-only). */
export function quarterOf(label: string): string | null {
    const m = label.match(/([A-Za-z]{3})[A-Za-z]*\s+\d{1,2},?\s*(\d{4})/);
    if (!m) return null;
    const month = MONTH_INDEX[m[1].toLowerCase()];
    if (month === undefined) return null;
    return `Q${Math.floor(month / 3) + 1} ${m[2]}`;
}

/** Phase an annual amount evenly over four quarters; rounding lands on Q4. */
function evenPhasing(amount: number): [number, number, number, number] {
    const q = Math.round(amount / 4);
    return [q, q, q, amount - q * 3];
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

// ---------------------------------------------------------------------------
// Segmentation — spec §4 decision order, derived per (contract, year).
// ---------------------------------------------------------------------------
export function deriveSegment(
    contract: Contract,
    year: number,
    materialityThreshold: Money
): Segment {
    // 1. Below materiality (or uncontracted) → Tail.
    if (contract.annualSpend.amount < materialityThreshold.amount) return "tail";

    const renewalYear =
        parseYear(contract.renewalDate) ?? parseYear(contract.endDate);

    // 2. A renewal/expiry event — or its notice window — falls inside Y.
    //    A long-lead renewal early in Y+1 opens its notice window in Y, so it's
    //    flagged now rather than missed.
    if (renewalYear === year) return "up_for_renewal";
    if (renewalYear === year + 1 && contract.noticePeriodDays >= 120) {
        return "up_for_renewal";
    }

    // 3. Contracted and locked through all of Y → Under contract.
    return "under_contract";
}

/** logo.dev image URL for a vendor brand domain. */
const LOGO_TOKEN = "pk_DmFwLlBkSAyEWWyE_dUI2Q";
export const logoUrlFor = (domain: string | undefined): string | undefined =>
    domain ? `https://img.logo.dev/${domain}?token=${LOGO_TOKEN}` : undefined;

// ---------------------------------------------------------------------------
// Renewal negotiation playbook
// ---------------------------------------------------------------------------
const STRATEGY_POOL: Array<Omit<CommercialStrategy, "savings">> = [
    { name: "Competitive benchmark reset", detail: "Re-quote against category P50" },
    { name: "Volume consolidation", detail: "Tier discount on committed volume" },
    { name: "Payment-terms extension", detail: "Net 30 → Net 60 · working-capital NPV" },
    { name: "Multi-year commitment", detail: "2–3 yr term for a price hold" },
    { name: "Auto-renewal removal", detail: "Strip the auto-renew uplift" },
    { name: "Usage right-sizing", detail: "Drop unused seats / capacity" },
];

const PRIORITY_POOL = [
    "Cap annual increase at CPI",
    "Termination for convenience (60d)",
    "Price benchmark review clause",
    "Volume rebate tier",
];

export function buildRenewalPlaybook(
    contract: Contract,
    rng: () => number
): RenewalPlaybook {
    const base = contract.annualSpend.amount;
    const startY = parseYear(contract.startDate);
    const endY = parseYear(contract.endDate);
    const termYears =
        startY !== null && endY !== null
            ? Math.max(1, Math.min(5, endY - startY))
            : 3;

    const paymentTermsLabel = ["Net 30", "Net 45", "Net 60"][
        Math.floor(rng() * 3)
    ];
    const leverageScore = Math.round(55 + rng() * 40); // 55–95

    // Total identified savings, 5–15% of annual spend, split across strategies.
    const expected = Math.round(base * (0.05 + rng() * 0.1));
    const picks = [...STRATEGY_POOL]
        .sort(() => rng() - 0.5)
        .slice(0, 3);
    const weights = picks.map(() => 0.5 + rng());
    const wsum = weights.reduce((a, b) => a + b, 0);
    let allocated = 0;
    const strategies: CommercialStrategy[] = picks.map((p, i) => {
        const amt =
            i === picks.length - 1
                ? expected - allocated
                : Math.round((expected * weights[i]) / wsum);
        allocated += amt;
        return { ...p, savings: usd(amt) };
    });

    const priorities: string[] = [];
    if (contract.autoRenew) priorities.push("Remove auto-renewal");
    if (!contract.increaseCapped) priorities.push("Cap annual increase at CPI");
    priorities.push(PRIORITY_POOL[Math.floor(rng() * PRIORITY_POOL.length)]);
    priorities.push("Termination for convenience (60d)");

    return {
        tcv: usd(base * termYears),
        termLabel: `${termYears}-year term`,
        paymentTermsLabel,
        autoRenew: contract.autoRenew,
        noticeDays: contract.noticePeriodDays,
        leverageScore,
        priorities: Array.from(new Set(priorities)).slice(0, 4),
        savingsLow: usd(Math.round(expected * 0.6)),
        savingsExpected: usd(expected),
        // Widen the high end per deal (1.4×–2.0× of expected) so the floor pulls
        // meaningfully away from the budget for some deals.
        savingsHigh: usd(
            Math.round(
                expected * (1.4 + (hashSeed(`${contract.id}:savhigh`) % 61) / 100)
            )
        ),
        strategies,
    };
}

// ---------------------------------------------------------------------------
// Line builders
// ---------------------------------------------------------------------------
interface BuildOpts {
    /** Populate actuals (current year only). */
    withActuals: boolean;
    /** Quarters elapsed so far this year (current year only). */
    elapsedQuarters: number;
}

/** One budget line for a material contract in `year`, or null if the contract
 * doesn't apply (expired before `year`, or it's tail — pooled elsewhere). */
export function buildBudgetLine(
    contract: Contract,
    year: number,
    materialityThreshold: Money,
    opts: BuildOpts
): BudgetLine | null {
    // Only model a contract in years it's actually live.
    const startYear = parseYear(contract.startDate);
    if (startYear !== null && startYear > year) return null;
    const endYear = parseYear(contract.endDate);
    if (endYear !== null && endYear < year) return null;

    const segment = deriveSegment(contract, year, materialityThreshold);
    if (segment === "tail") return null; // handled by buildTailPoolLines

    const base = contract.annualSpend.amount;
    const rng = mulberry32(hashSeed(`${contract.id}:${year}`));

    let plan: number;
    let range: BudgetLine["range"];
    let method: BudgetLine["method"];
    let rationale: string;
    let increasePct: number | undefined;
    let increaseKind: BudgetLine["increaseKind"];

    if (segment === "under_contract") {
        method = "contracted_escalator";
        // No renewal this year, but planned usage growth still moves spend.
        const usage = Math.round((0.02 + rng() * 0.08) * 100) / 100; // 2–10%
        increasePct = usage;
        increaseKind = "usage";
        plan = Math.round(base * (1 + usage));
        rationale = `Planned usage growth ${formatPct(usage)} · locked, no renewal`;
    } else {
        method = "renewal_range";
        if (contract.increaseCapped) {
            const cap = contract.escalatorPct || 0.04;
            increasePct = cap;
            increaseKind = "capped";
            const expected = Math.round(base * (1 + cap));
            const floor = Math.round(base * (1 + cap * 0.5));
            const worst = Math.round(base * (1 + cap + 0.015));
            plan = expected;
            range = { worst: usd(worst), expected: usd(expected), floor: usd(floor) };
            rationale = `Capped renewal · cap ${formatPct(cap)}`;
        } else {
            const market = 0.03 + rng() * 0.09; // benchmarked +3–12%
            increasePct = Math.round(market * 100) / 100;
            increaseKind = "market";
            const worstU = market + 0.04 + rng() * 0.03;
            const floorU = 0.01 + rng() * 0.03; // mitigated outcome
            const expected = Math.round(base * (1 + market));
            const worst = Math.round(base * (1 + worstU));
            const floor = Math.round(base * (1 + floorU));
            plan = expected;
            range = { worst: usd(worst), expected: usd(expected), floor: usd(floor) };
            rationale = `Benchmarked market ${formatPct(market)} · mitigation to ${formatPct(
                floorU
            )} · playbook generated`;
        }
    }

    // Only renewals carry a savings playbook — under-contract lines aren't up
    // for renewal this year, so there's nothing to negotiate.
    const playbook =
        segment === "up_for_renewal"
            ? buildRenewalPlaybook(contract, rng)
            : undefined;

    const phasing = evenPhasing(plan);
    const line: BudgetLine = {
        id: budgetLineId(`bl-${contract.id}-${year}`),
        year,
        segment,
        method,
        label: contract.vendorName,
        categoryId: contract.categoryId,
        categoryName: contract.categoryName,
        contractId: contract.id,
        logoUrl: logoUrlFor(contract.domain),
        startDate: contract.startDate,
        endDate: contract.endDate,
        closeQuarter:
            segment === "up_for_renewal"
                ? quarterOf(contract.renewalDate) ?? quarterOf(contract.endDate) ?? undefined
                : undefined,
        capped: contract.increaseCapped,
        playbook,
        currentSpend: contract.annualSpend.amount,
        increasePct,
        increaseKind,
        planAmount: usd(plan),
        range,
        rationale,
        owner: segment === "up_for_renewal" ? "Procurement · category lead" : "FP&A",
        phasing,
        segmentTag: SEGMENT_META[segment],
    };

    if (opts.withActuals) attachActuals(line, rng, opts.elapsedQuarters);
    return line;
}

/** Re-forecast a line off an edited increase (market prediction or planned
 * usage growth). The current spend is the base. */
export function withIncrease(line: BudgetLine, pct: number): BudgetLine {
    const base = line.currentSpend ?? line.planAmount.amount;
    const expected = Math.round(base * (1 + pct));
    const worst = Math.round(base * (1 + pct + 0.04));
    const floor = Math.round(base * (1 + Math.max(0.01, pct - 0.05)));
    const label = line.increaseKind === "usage" ? "Usage growth" : "Market increase";
    return {
        ...line,
        increasePct: pct,
        planAmount: usd(expected),
        range: { worst: usd(worst), expected: usd(expected), floor: usd(floor) },
        phasing: evenPhasing(expected),
        rationale: `${label} ${formatPct(pct)} · analyst estimate`,
    };
}

export interface InitiativeSpec {
    key: string;
    name: string;
    categoryKey: string;
    categoryId: Category["id"];
    categoryName: string;
    /** Discretionary reserve / pipeline amount. */
    expected: number;
    low: number;
    high: number;
    /** Quarter the initiative is expected to land (1–4). */
    quarter: number;
    rationale: string;
    owner: string;
}

/** A named new-spend initiative (spec §5 — the relief valve). Forecast year
 * only; carries an explicit range. */
export function buildInitiativeLine(
    spec: InitiativeSpec,
    year: number
): BudgetLine {
    const phasing = evenPhasing(spec.expected);
    return {
        id: budgetLineId(`bl-init-${spec.key}-${year}`),
        year,
        segment: "new_spend",
        method: "initiative",
        label: spec.name,
        categoryId: spec.categoryId,
        categoryName: spec.categoryName,
        closeQuarter: `Q${spec.quarter} ${year}`,
        planAmount: usd(spec.expected),
        range: {
            worst: usd(spec.high),
            expected: usd(spec.expected),
            floor: usd(spec.low),
        },
        rationale: spec.rationale,
        owner: spec.owner,
        phasing,
        segmentTag: SEGMENT_META.new_spend,
    };
}

export interface NewSpendSpec {
    id: string;
    name: string;
    categoryId: Category["id"];
    categoryName: string;
    amount: number;
    /** Quarter the spend is expected to close — e.g. "Q3 2027". */
    closeQuarter: string;
    confidence: Confidence;
    owner: string;
    rationale: string;
}

/** A manually-added new-spend line (no benchmarked range — a single number the
 * user committed to). */
export function buildNewSpendLine(spec: NewSpendSpec, year: number): BudgetLine {
    return {
        id: budgetLineId(spec.id),
        year,
        segment: "new_spend",
        method: "initiative",
        label: spec.name,
        categoryId: spec.categoryId,
        categoryName: spec.categoryName,
        closeQuarter: spec.closeQuarter,
        confidence: spec.confidence,
        planAmount: usd(spec.amount),
        rationale: spec.rationale,
        owner: spec.owner,
        phasing: evenPhasing(spec.amount),
        segmentTag: SEGMENT_META.new_spend,
    };
}

/** A newly-signed contract — new spend (not a renewal), so no increase. It may
 * carry a savings playbook to negotiate the new contract down. */
export function buildNewContractLine(
    contract: Contract,
    year: number,
    withPlaybook: boolean
): BudgetLine {
    const rng = mulberry32(hashSeed(`${contract.id}:${year}:nc`));
    const amount = contract.annualSpend.amount;
    const q = 1 + (hashSeed(`${contract.id}:q`) % 4);
    return {
        id: budgetLineId(`bl-nc-${contract.id}-${year}`),
        year,
        segment: "new_spend",
        method: "initiative",
        label: contract.vendorName,
        categoryId: contract.categoryId,
        categoryName: contract.categoryName,
        contractId: contract.id,
        logoUrl: logoUrlFor(contract.domain),
        closeQuarter: `Q${q} ${year}`,
        confidence: withPlaybook ? "medium" : "low",
        playbook: withPlaybook ? buildRenewalPlaybook(contract, rng) : undefined,
        planAmount: usd(amount),
        rationale: "Newly-signed contract",
        owner: "Procurement",
        phasing: evenPhasing(amount),
        segmentTag: SEGMENT_META.new_spend,
    };
}

/** Playbook for a real negotiation project — TCV, savings range and score are
 * the real values; the strategy/priority text is generic (split of the real
 * expected savings). */
function buildProjectPlaybook(p: WpcProject): RenewalPlaybook {
    const rng = mulberry32(hashSeed(`${p.projectId}:pb`));
    const startY = parseYear(p.startDate);
    const endY = p.endDate ? parseYear(p.endDate) : null;
    const termYears =
        startY !== null && endY !== null
            ? Math.max(1, Math.min(5, endY - startY))
            : 3;

    const expected = p.savingsExpected.amount;
    const picks = [...STRATEGY_POOL].sort(() => rng() - 0.5).slice(0, 3);
    const weights = picks.map(() => 0.5 + rng());
    const wsum = weights.reduce((a, b) => a + b, 0);
    let allocated = 0;
    const strategies: CommercialStrategy[] = picks.map((s, i) => {
        const amt =
            i === picks.length - 1
                ? expected - allocated
                : Math.round((expected * weights[i]) / wsum);
        allocated += amt;
        return { ...s, savings: usd(amt) };
    });

    const priorities: string[] = [];
    if (p.autoRenew) priorities.push("Remove auto-renewal");
    priorities.push("Cap annual increase at CPI");
    priorities.push(PRIORITY_POOL[Math.floor(rng() * PRIORITY_POOL.length)]);
    priorities.push("Termination for convenience (60d)");

    return {
        tcv: p.tcv,
        termLabel: `${termYears}-year term`,
        paymentTermsLabel: ["Net 30", "Net 45", "Net 60"][Math.floor(rng() * 3)],
        autoRenew: p.autoRenew,
        noticeDays: 90,
        leverageScore: p.score,
        priorities: Array.from(new Set(priorities)).slice(0, 4),
        savingsLow: p.savingsLow,
        savingsExpected: p.savingsExpected,
        savingsHigh: p.savingsHigh,
        strategies,
    };
}

/** A forecast line backed by a real negotiation project — rendered like a
 * new-contract row (read-only) with a playbook carrying the real TCV / savings /
 * score, and a `projectId` so the drawer can deep-link to the live project. */
export function buildProjectLine(
    p: WpcProject,
    year: number,
    categoryId: CategoryId,
    categoryName: string
): BudgetLine {
    const amount = p.annual.amount;
    const q = 1 + (hashSeed(`${p.projectId}:q`) % 4);
    return {
        id: budgetLineId(`bl-proj-${p.projectId}-${year}`),
        year,
        segment: "new_spend",
        method: "initiative",
        label: p.vendorName,
        categoryId,
        categoryName,
        contractId: contractId(`proj-${p.projectId}`),
        projectId: p.projectId,
        logoUrl: p.logoUrl,
        closeQuarter: `Q${q} ${year}`,
        confidence: "medium",
        playbook: buildProjectPlaybook(p),
        planAmount: usd(amount),
        rationale: "Linked negotiation project",
        owner: "Procurement",
        phasing: evenPhasing(amount),
        segmentTag: SEGMENT_META.new_spend,
    };
}

/** Post deterministic actuals for the elapsed quarters: plan-phase ± a small
 * variance drawn from the line's PRNG. */
function attachActuals(line: BudgetLine, rng: () => number, elapsed: number): void {
    const variance = -0.03 + rng() * 0.09; // −3% … +6%
    const actualPhasing = line.phasing
        .slice(0, elapsed)
        .map((q) => Math.round(q * (1 + variance)));
    line.actualPhasing = actualPhasing;
    line.actualAmount = usd(sum(actualPhasing));
}
